import { useState, useRef, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { ScrollArea } from "@/components/ui/scroll-area";
import { X, Plus, Check, User, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ContactUI } from "@/types/ui/customer-detail";

interface MultiEmailSelectorProps {
  label: string;
  selectedEmails: string[];
  onEmailsChange: (emails: string[]) => void;
  contacts?: ContactUI[];
  defaultEmail?: string;
  required?: boolean;
  placeholder?: string;
  helperText?: string;
}

export function MultiEmailSelector({
  label,
  selectedEmails,
  onEmailsChange,
  contacts = [],
  defaultEmail,
  required = false,
  placeholder = "Add recipient...",
  helperText,
}: Readonly<MultiEmailSelectorProps>) {
  const [open, setOpen] = useState(false);
  const [customEmail, setCustomEmail] = useState("");
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);

  // Focus custom input when shown
  useEffect(() => {
    if (showCustomInput && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [showCustomInput]);

  const handleRemoveEmail = (emailToRemove: string) => {
    // Don't allow removing the last email if required
    if (required && selectedEmails.length === 1) {
      return;
    }
    onEmailsChange(selectedEmails.filter((email) => email !== emailToRemove));
  };

  const handleAddContact = (contact: ContactUI) => {
    if (!selectedEmails.includes(contact.email)) {
      onEmailsChange([...selectedEmails, contact.email]);
    }
    setSearchQuery("");
  };

  const handleAddCustomEmail = () => {
    const trimmedEmail = customEmail.trim();
    if (trimmedEmail && isValidEmail(trimmedEmail) && !selectedEmails.includes(trimmedEmail)) {
      onEmailsChange([...selectedEmails, trimmedEmail]);
      setCustomEmail("");
      setShowCustomInput(false);
      setOpen(false);
    }
  };

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const getContactName = (email: string) => {
    const contact = contacts.find((c) => c.email === email);
    if (contact) {
      return `${contact.firstName} ${contact.surname}`;
    }
    return null;
  };

  const isPrimaryContact = (email: string) => {
    const contact = contacts.find((c) => c.email === email);
    return contact?.isPrimary || email === defaultEmail;
  };

  const filteredContacts = contacts.filter((contact) => {
    const fullName = `${contact.firstName} ${contact.surname}`.toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      fullName.includes(query) ||
      contact.email.toLowerCase().includes(query) ||
      contact.jobTitle?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="grid gap-2">
      <Label htmlFor={`email-selector-${label}`}>
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>
      <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-background min-h-[42px]">
        {selectedEmails.map((email) => {
          const contactName = getContactName(email);
          const isPrimary = isPrimaryContact(email);
          const canRemove = !required || selectedEmails.length > 1;

          return (
            <Badge
              key={email}
              variant={isPrimary ? "default" : "secondary"}
              className={cn(
                "pl-2 pr-1 py-1 gap-1 text-sm",
                isPrimary && "border-primary"
              )}
            >
              <Mail className="h-3 w-3" />
              <span className="max-w-[200px] truncate">
                {contactName ? `${contactName} <${email}>` : email}
              </span>
              {canRemove && (
                <button
                  type="button"
                  onClick={() => handleRemoveEmail(email)}
                  className="ml-1 rounded-full hover:bg-muted p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </Badge>
          );
        })}

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-muted-foreground hover:text-foreground"
            >
              <Plus className="h-4 w-4 mr-1" />
              {placeholder}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search contacts..."
                value={searchQuery}
                onValueChange={setSearchQuery}
              />
              <CommandList>
                <CommandEmpty>No contacts found.</CommandEmpty>
                {filteredContacts.length > 0 && (
                  <CommandGroup heading="Customer Contacts">
                    <ScrollArea className="h-[200px]">
                      {filteredContacts.map((contact) => {
                        const isSelected = selectedEmails.includes(contact.email);
                        return (
                          <CommandItem
                            key={contact.id}
                            onSelect={() => handleAddContact(contact)}
                            className="flex items-center gap-2 cursor-pointer"
                          >
                            <div
                              className={cn(
                                "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                                isSelected
                                  ? "bg-primary text-primary-foreground"
                                  : "opacity-50 [&_svg]:invisible"
                              )}
                            >
                              <Check className="h-3 w-3" />
                            </div>
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
                                <User className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate">
                                    {contact.firstName} {contact.surname}
                                  </span>
                                  {contact.isPrimary && (
                                    <Badge variant="outline" className="text-xs">
                                      Primary
                                    </Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground truncate">
                                  {contact.jobTitle && <span>{contact.jobTitle} • </span>}
                                  {contact.email}
                                </div>
                              </div>
                            </div>
                          </CommandItem>
                        );
                      })}
                    </ScrollArea>
                  </CommandGroup>
                )}
                <CommandSeparator />
                <CommandGroup>
                  {!showCustomInput ? (
                    <CommandItem
                      onSelect={() => setShowCustomInput(true)}
                      className="cursor-pointer"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add custom email address
                    </CommandItem>
                  ) : (
                    <div className="p-2 space-y-2">
                      <Input
                        ref={customInputRef}
                        type="email"
                        placeholder="email@example.com"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddCustomEmail();
                          } else if (e.key === "Escape") {
                            setShowCustomInput(false);
                            setCustomEmail("");
                          }
                        }}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleAddCustomEmail}
                          disabled={!customEmail.trim() || !isValidEmail(customEmail.trim())}
                        >
                          Add
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowCustomInput(false);
                            setCustomEmail("");
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      {required && selectedEmails.length === 0 && (
        <p className="text-sm text-destructive">At least one recipient is required</p>
      )}
      {helperText && (
        <p className="text-sm text-muted-foreground">{helperText}</p>
      )}
    </div>
  );
}
